import React, { createContext, useState, useContext, useEffect, ReactNode } from 'react';
import { User, Child } from '../types';
import { SESSION_KEY_LOCATION_ID, SESSION_KEY_USER_DATA } from '../constants';
import registerChildren from '../hooks/user/registerChildren';
import updateChild from '../hooks/user/updateChild';
import { mapChildToApiFormat } from '../utils';

interface UserContextType {
    user: User | null;
    /** True only after initial restore from localStorage has run (avoids flash of login UI). */
    isUserLoaded: boolean;
    saveUser: (userData: User) => Promise<boolean>;
    addChildToUser: (childData: Child) => void;
    handleDeleteChild: (childId: number) => void;
    updateChildInUser: (
        childId: number,
        updates: Partial<Pick<Child, 'fname' | 'lname' | 'dob'>>,
        onSuccess?: () => void | Promise<void>,
    ) => Promise<boolean>;
    updateUserAndSession: (userData: User) => void;
    resetUserContext: () => void;
    selectedChild: Child | null;
    setSelectedChild: (child: Child | null) => void;
}

const UserContext = createContext<UserContextType | undefined>(undefined);

interface UserProviderProps {
    children: ReactNode;
}

export const UserProvider: React.FC<UserProviderProps> = ({ children }) => {
    const [user, setUser] = useState<User | null>(null);
    const [selectedChild, setSelectedChildState] = useState<Child | null>(null);
    const [isUserLoaded, setIsUserLoaded] = useState(false);

    useEffect(() => {
        const storedUserData = localStorage.getItem(SESSION_KEY_USER_DATA);
        if (storedUserData) {
            setUser(JSON.parse(storedUserData));
        }
        setIsUserLoaded(true);
    }, []);

    // Default to first child (or restore persisted selection) and keep user.selectedChild in sync so
    // EventPage / EventPricingTiersBoxes always see a selected child when user has children.
    useEffect(() => {
        if (!user?.children?.length) return;
        // Always resolve from the current children array by ID to pick up any field updates (e.g. DOB).
        const storedId = user.selectedChild?.id;
        const freshFromArray = storedId
            ? user.children.find((c) => c.id === storedId)
            : undefined;
        const childToSelect = freshFromArray ?? user.children[0];
        setSelectedChildState(childToSelect);
        const needSyncUser =
            !user.selectedChild ||
            user.selectedChild.id !== childToSelect.id ||
            user.selectedChild.dob !== childToSelect.dob;
        if (needSyncUser) {
            setUser((prev) => {
                if (!prev) return prev;
                const next = { ...prev, selectedChild: childToSelect };
                localStorage.setItem(SESSION_KEY_USER_DATA, JSON.stringify(next));
                return next;
            });
        }
    }, [user?.uid, user?.children?.length, user?.children?.map((c) => `${c.id}:${c.dob}`).join(',')]);

    const updateUserAndSession = (updatedUser: User) => {
        setUser((prev) => {
            const mappedUser = {
                ...updatedUser,
                children: updatedUser.children.map(child => ({ ...child, isNew: false })),
            };
            // Re-derive selectedChild from the fresh children array by ID so stale DOB is never preserved.
            const prevSelectedId = prev?.selectedChild?.id;
            const freshSelected = prevSelectedId
                ? mappedUser.children.find((c) => c.id === prevSelectedId)
                : undefined;
            const toStore = freshSelected
                ? { ...mappedUser, selectedChild: freshSelected }
                : mappedUser;
            localStorage.setItem(SESSION_KEY_USER_DATA, JSON.stringify(toStore));
            return toStore;
        });
    };

    const resetUserContext = () => {
        setUser(null);
        setSelectedChildState(null);
    };

    const saveUser = async (userData: User): Promise<boolean> => {
        const newChildren = userData.children.filter((child) => child.isNew === true);
        const oldChildren = userData.children.filter((child) => child.isNew !== true);
        const location = localStorage.getItem(SESSION_KEY_LOCATION_ID);
        const newChildrenData = newChildren.map((child) => mapChildToApiFormat(child, location));

        try {
            let newChildIds: number[] | null = [];

            if (newChildrenData.length > 0) {
                const registerChildrenResponse = await registerChildren(userData.uid, newChildrenData);

                if (registerChildrenResponse.success) {
                    newChildIds = registerChildrenResponse.data;
                } else {
                    return false;
                }
            }

            const updatedUser = {
                ...userData,
                children: [
                    ...oldChildren,
                    ...newChildren
                        .filter((_, index) => newChildIds && index < newChildIds.length)
                        .map((child, index) => ({
                            ...child,
                            id: newChildIds ? newChildIds[index] : child.id,
                            isNew: false
                        }))
                ],
            };

            updateUserAndSession(updatedUser);
            return true;
        } catch (error) {
            return false;
        }
    };

    const addChildToUser = (childData: Child) => {
        setUser((currentUser) => {
            if (currentUser) {
                const updatedUser = {
                    ...currentUser,
                    children: [...currentUser.children, childData],
                };
                return updatedUser;
            }
            return currentUser;
        });
    };

    const handleDeleteChild = (childId: number) => {
        setUser((currentUser) => {
            if (currentUser) {
                const updatedUser = {
                    ...currentUser,
                    children: currentUser.children.filter((child) => child.id !== childId),
                };
                return updatedUser;
            }
            return currentUser;
        });
    };

    /**
     * Updates a child's details via API, then syncs context and session on success.
     * Accepts an optional `onSuccess` callback (e.g. `fetchCart`) that runs after
     * state is updated so dependent data is refreshed with the corrected child DOB.
     */
    const updateChildInUser = async (
        childId: number,
        updates: Partial<Pick<Child, 'fname' | 'lname' | 'dob'>>,
        onSuccess?: () => void | Promise<void>,
    ): Promise<boolean> => {
        const { success } = await updateChild(childId, updates);

        if (!success) {
            return false;
        }

        setUser((currentUser) => {
            if (!currentUser) return currentUser;
            const updatedChildren = currentUser.children.map((child) =>
                child.id === childId ? { ...child, ...updates, isNew: false } : child
            );
            // Re-derive selectedChild from the updated array so the fresh DOB is reflected.
            const freshSelected = currentUser.selectedChild?.id === childId
                ? updatedChildren.find((c) => c.id === childId) ?? currentUser.selectedChild
                : currentUser.selectedChild;
            const updatedUser = { ...currentUser, children: updatedChildren, selectedChild: freshSelected };
            localStorage.setItem(SESSION_KEY_USER_DATA, JSON.stringify(updatedUser));
            return updatedUser;
        });

        // Sync selectedChild state from the updated children array (avoids stale object spread).
        if (selectedChild?.id === childId) {
            setSelectedChildState((prev) => {
                if (!prev) return null;
                return { ...prev, ...updates };
            });
        }

        if (onSuccess) {
            await onSuccess();
        }

        return true;
    };

    const updateSelectedChild = (child: Child | null) => {
        setSelectedChildState(child);
        if (user && child) {
            const updatedUser = { ...user, selectedChild: child };
            setUser(updatedUser);
            localStorage.setItem(SESSION_KEY_USER_DATA, JSON.stringify(updatedUser));
        }
    };

    return (
        <UserContext.Provider value={{
            user,
            isUserLoaded,
            saveUser,
            addChildToUser,
            handleDeleteChild,
            updateChildInUser,
            updateUserAndSession,
            resetUserContext,
            selectedChild,
            setSelectedChild: updateSelectedChild
        }}>
            {children}
        </UserContext.Provider>
    );
};

export const useUser = () => {
    const context = useContext(UserContext);
    if (context === undefined) {
        throw new Error('useUser must be used within a UserProvider');
    }
    return context;
};
